/**
 * @file Design Classes Feature
 *
 * When ?injectDesignClasses query param is present, adds CSS classes to Vue component
 * root elements for design tooling purposes.
 *
 * Class naming convention:
 * - Single root: `design-{kebab-case-component-name}`
 * - Multiple roots: `design-{name}` + `design-{name}-{index}` on each element
 */
import window from '@@/bits/global'
import { kebabCase } from 'es-toolkit'
import { Fragment, Teleport, type App, type ComponentPublicInstance, type VNode } from 'vue'

let designClassesEnabled: boolean | null = null

function isDesignClassesEnabled(): boolean {
  if (designClassesEnabled === null) {
    designClassesEnabled = new URLSearchParams(window.location.search).has('injectDesignClasses')
  }
  return designClassesEnabled
}

function collectElementsFromVNode(vnode: VNode | null | undefined, elements: Element[]): void {
  if (vnode == null) return

  // Handle Fragment (multiple root elements) and Teleport
  if ((vnode.type === Fragment || vnode.type === Teleport) && Array.isArray(vnode.children)) {
    for (const child of vnode.children) {
      if (child != null && typeof child === 'object') {
        collectElementsFromVNode(child as VNode, elements)
      }
    }
    return
  }

  // Handle regular element
  if (vnode.el instanceof Element) {
    elements.push(vnode.el)
  }
}

function getComponentRootElements(instance: ComponentPublicInstance): Element[] {
  const elements: Element[] = []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const subTree = (instance.$ as any).subTree as VNode | undefined
  if (subTree == null) return elements

  collectElementsFromVNode(subTree, elements)
  return elements
}

/**
 * Registers a mixin that adds design classes to component root elements.
 * Only registers if ?injectDesignClasses query param is present.
 */
function applyDesignClasses(instance: ComponentPublicInstance): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const componentName = (instance.$.type as any).name ?? (instance.$.type as any).__name
  if (componentName == null) return // Skip anonymous components

  const baseClass = `design-${kebabCase(componentName)}`
  const elements = getComponentRootElements(instance)

  if (elements.length === 1) {
    if (!elements[0].classList.contains(baseClass)) {
      elements[0].classList.add(baseClass)
    }
  } else if (elements.length > 1) {
    elements.forEach((el, index) => {
      const indexedClass = `${baseClass}-${index}`
      if (!el.classList.contains(baseClass)) {
        el.classList.add(baseClass, indexedClass)
      }
    })
  }
}

function injectDesignClassesIfEnabled(app: App): void {
  if (!isDesignClassesEnabled()) return

  app.mixin({
    mounted() {
      applyDesignClasses(this)
    },
    updated() {
      // Reapply classes when root elements change (e.g., v-if/v-else swap, dynamic fragments)
      applyDesignClasses(this)
    },
  })
}
export { injectDesignClassesIfEnabled }
